import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, CheckCircle, Eye, EyeOff, Loader2, Lock, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '../../services/auth';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { getAssetUrl } from '../../utils/url';

interface PasswordStrength {
  score: number;
  label: string;
  colorClass: string;
  barClass: string;
}

const getPasswordStrength = (password: string): PasswordStrength => {
  let score = 0;

  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { score: 1, label: 'Lemah', colorClass: 'text-red-500', barClass: 'bg-red-500' };
  if (score === 2) return { score: 2, label: 'Sedang', colorClass: 'text-amber-500', barClass: 'bg-amber-500' };
  if (score === 3) return { score: 3, label: 'Kuat', colorClass: 'text-sky-500', barClass: 'bg-sky-500' };
  return { score: 4, label: 'Sangat Kuat', colorClass: 'text-emerald-500', barClass: 'bg-emerald-500' };
};

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [focusedField, setFocusedField] = useState<'password' | 'confirmation' | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [logoError, setLogoError] = useState(false);

  const { settings, fetchPublicSettings } = useSystemSettingsStore();

  useEffect(() => {
    fetchPublicSettings();
  }, [fetchPublicSettings]);

  useEffect(() => {
    setLogoError(false);
  }, [settings.logo_url]);

  const appName = settings.app_name || 'SIM-EVAL';
  const institutionName = settings.institution_name || 'Universitas Buana Perjuangan Karawang';
  const readableFooterText = settings.footer_text || 'Copyright 2026 SIM-EVAL UBP Karawang. Hak cipta dilindungi.';
  const logoUrl = getAssetUrl(settings.logo_url);
  const showLogo = Boolean(logoUrl && !logoError);

  const brandInitials = appName
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

  useEffect(() => {
    if (appName) {
      document.title = `Reset Password - ${appName}`;
    }

    if (settings.favicon_url) {
      const faviconUrl = getAssetUrl(settings.favicon_url);
      if (faviconUrl) {
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = faviconUrl;
      }
    }
  }, [appName, settings.favicon_url]);

  const strength = password ? getPasswordStrength(password) : null;
  const passwordsMatch = passwordConfirmation !== '' && password === passwordConfirmation;

  const requirements = [
    { label: 'Minimal 8 karakter', met: password.length >= 8 },
    { label: 'Huruf besar & kecil', met: /[A-Z]/.test(password) && /[a-z]/.test(password) },
    { label: 'Mengandung angka', met: /[0-9]/.test(password) },
    { label: 'Karakter spesial', met: /[^A-Za-z0-9]/.test(password) },
  ];

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};

    if (!token) {
      nextErrors.token = 'Token reset tidak valid. Silakan minta link baru.';
    }

    if (!email) {
      nextErrors.email = 'Email tidak ditemukan. Silakan minta link baru.';
    }

    if (!password) {
      nextErrors.password = 'Password baru wajib diisi';
    } else if (password.length < 8) {
      nextErrors.password = 'Password minimal 8 karakter';
    }

    if (!passwordConfirmation) {
      nextErrors.password_confirmation = 'Konfirmasi password wajib diisi';
    } else if (password !== passwordConfirmation) {
      nextErrors.password_confirmation = 'Password tidak cocok';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await authService.resetPassword({
        token,
        email,
        password,
        password_confirmation: passwordConfirmation,
      });

      setSuccess(true);
      toast.success('Password berhasil direset!');
    } catch (err: any) {
      if (err.response?.status === 422) {
        const validationErrors = err.response.data?.errors || {};
        const mappedErrors: Record<string, string> = {};

        Object.keys(validationErrors).forEach((key) => {
          mappedErrors[key] = validationErrors[key][0] || validationErrors[key];
        });

        setErrors(mappedErrors);
      } else if (err.response?.status === 400 || err.response?.data?.message?.includes('token')) {
        setErrors({ token: 'Token sudah kedaluwarsa atau tidak valid. Silakan minta link baru.' });
      } else {
        toast.error(err.response?.data?.message || 'Terjadi kesalahan. Silakan coba lagi.');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputWrapperClass = (field: 'password' | 'confirmation') =>
    `relative rounded-xl transition-all duration-200 ${focusedField === field ? 'ring-2 ring-[#0f33ab]/20' : ''}`;

  const iconPanelClass = (field: 'password' | 'confirmation') =>
    `absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center rounded-l-xl transition-colors ${
      focusedField === field ? 'bg-[#0f33ab] text-white' : 'bg-gray-100 text-gray-400'
    }`;

  const inputClass = (field: 'password' | 'confirmation', hasError: boolean) =>
    `w-full pl-14 pr-12 py-3.5 rounded-xl border-2 text-sm font-medium bg-white focus:outline-none transition-all ${
      hasError
        ? 'border-red-300'
        : focusedField === field
          ? 'border-[#0f33ab]'
          : 'border-gray-200 hover:border-gray-300'
    }`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8f9fb] relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#0f33ab] via-[#1a4fd6] to-[#0f33ab]" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#0f33ab]/[0.02] rounded-full -mr-48 -mt-48" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#0f33ab]/[0.03] rounded-full -ml-32 -mb-32" />

      <div className="relative z-10 w-full max-w-[440px] px-6 py-8">
        <div className="text-center mb-8">
          <div
            className={`inline-flex items-center justify-center w-16 h-16 mb-4 overflow-hidden rounded-2xl ${
              showLogo ? 'bg-transparent' : 'bg-[#0f33ab] shadow-lg shadow-[#0f33ab]/30'
            }`}
          >
            {showLogo ? (
              <img src={logoUrl!} alt="Logo" className="w-full h-full object-contain" onError={() => setLogoError(true)} />
            ) : (
              <span className="text-2xl font-black text-white">{brandInitials}</span>
            )}
          </div>
          <h1 className="text-xl font-black text-[#0f33ab] tracking-tight">{appName}</h1>
          <p className="text-xs text-gray-500 mt-1">{institutionName}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
          {!success ? (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#0f33ab]/5 rounded-xl mb-3">
                  <Shield className="w-6 h-6 text-[#0f33ab]" />
                </div>
                <h2 className="text-xl font-black text-gray-900">Reset Password</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Buat password baru untuk akun{' '}
                  {email ? <span className="font-semibold text-[#0f33ab]">{email}</span> : 'Anda'}.
                </p>
              </div>

              {(errors.token || errors.email) && (
                <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                  <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-600">{errors.token || errors.email}</p>
                    <Link to="/forgot-password" className="inline-flex items-center gap-1 text-xs font-semibold text-[#0f33ab] hover:underline mt-2">
                      Minta link reset baru
                    </Link>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Password Baru
                  </label>
                  <div className={inputWrapperClass('password')}>
                    <div className={iconPanelClass('password')}>
                      <Lock className="w-4 h-4" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) {
                          setErrors((prev) => ({ ...prev, password: '' }));
                        }
                      }}
                      onFocus={() => setFocusedField('password')}
                      onBlur={() => setFocusedField((current) => (current === 'password' ? null : current))}
                      placeholder="Masukkan password baru"
                      className={inputClass('password', Boolean(errors.password))}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#0f33ab] transition-colors"
                      aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-xs text-red-500 font-medium flex items-center gap-1">
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                      {errors.password}
                    </p>
                  )}

                  {password && strength && (
                    <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 p-3 space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 flex gap-1">
                          {[1, 2, 3, 4].map((level) => (
                            <div
                              key={level}
                              className={`h-1.5 flex-1 rounded-full transition-colors ${
                                level <= strength.score ? strength.barClass : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                        <span className={`text-xs font-semibold ${strength.colorClass}`}>{strength.label}</span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {requirements.map((requirement) => (
                          <div key={requirement.label} className="flex items-center gap-2">
                            <CheckCircle className={`w-3.5 h-3.5 ${requirement.met ? 'text-emerald-500' : 'text-gray-300'}`} />
                            <span className={`text-[11px] ${requirement.met ? 'text-emerald-600' : 'text-gray-500'}`}>
                              {requirement.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Konfirmasi Password
                  </label>
                  <div className={inputWrapperClass('confirmation')}>
                    <div className={iconPanelClass('confirmation')}>
                      <Lock className="w-4 h-4" />
                    </div>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordConfirmation}
                      onChange={(e) => {
                        setPasswordConfirmation(e.target.value);
                        if (errors.password_confirmation) {
                          setErrors((prev) => ({ ...prev, password_confirmation: '' }));
                        }
                      }}
                      onFocus={() => setFocusedField('confirmation')}
                      onBlur={() => setFocusedField((current) => (current === 'confirmation' ? null : current))}
                      placeholder="Ulangi password baru"
                      className={inputClass('confirmation', Boolean(errors.password_confirmation))}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((value) => !value)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#0f33ab] transition-colors"
                      aria-label={showConfirmPassword ? 'Sembunyikan konfirmasi password' : 'Tampilkan konfirmasi password'}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password_confirmation && (
                    <p className="text-xs text-red-500 font-medium flex items-center gap-1">
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                      {errors.password_confirmation}
                    </p>
                  )}

                  {passwordConfirmation !== '' && (
                    <div className="flex items-center gap-2 text-[11px] font-medium mt-2">
                      {passwordsMatch ? (
                        <>
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="text-emerald-600">Password cocok</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                          <span className="text-red-600">Password tidak cocok</span>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !token || !email}
                  className={`relative w-full py-3.5 rounded-xl font-bold text-sm text-white overflow-hidden group transition-all duration-300 ${
                    loading || !token || !email
                      ? 'bg-[#0f33ab]/70 cursor-not-allowed'
                      : 'bg-[#0f33ab] hover:shadow-lg hover:shadow-[#0f33ab]/30 active:scale-[0.98]'
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <span className="relative flex items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Menyimpan...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </span>
                </button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-50 rounded-full mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-black text-gray-900">Password Berhasil Direset!</h2>
              <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                Password Anda telah berhasil diperbarui. Silakan login dengan password baru.
              </p>

              <button
                onClick={() => navigate('/login')}
                className="relative mt-6 w-full py-3.5 rounded-xl font-bold text-sm text-white overflow-hidden group transition-all duration-300 bg-[#0f33ab] hover:shadow-lg hover:shadow-[#0f33ab]/30 active:scale-[0.98]"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative">Masuk ke Sistem</span>
              </button>
            </div>
          )}

          {!success && (
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0f33ab] hover:text-[#0a2478] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Kembali ke halaman login
              </Link>
            </div>
          )}
        </div>

        <div className="mt-6 text-center space-y-2">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
            <Shield className="w-3.5 h-3.5" />
            <span>Akses dilindungi koneksi terenkripsi</span>
          </div>
          <p className="text-xs text-gray-400">{readableFooterText}</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
